/**
 * Disponibilita' delle persone (regole pure, testate in __tests__/availability.test.ts).
 *
 * Tutto lavora in minuti dalla mezzanotte del giorno scelto (ora locale del browser).
 * L'orario di lavoro NON viene dagli "slot di lavoro" del calendario (quelli sono blocchi di
 * pianificazione dei progetti, non orari): si usa un orario di base, lunedi-venerdi 9:00-18:00.
 */
export interface Interval { start: number; end: number }
export interface TimedItem { start_time: string | Date; end_time: string | Date; all_day?: boolean; ownerId?: string | null }

export const DAY_MINUTES = 24 * 60;
export const DEFAULT_WORK_HOURS: Interval = { start: 9 * 60, end: 18 * 60 };
export const MIN_FREE_MINUTES = 15;

function startOfDay(day: Date): number {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
}

/** Orario di lavoro del giorno: null nei fine settimana. */
export function workWindow(day: Date, hours: Interval = DEFAULT_WORK_HOURS): Interval | null {
  const d = day.getDay();
  return d === 0 || d === 6 ? null : { ...hours };
}

/** Porta un periodo dentro il giorno (in minuti). null se non tocca quel giorno o se dura zero. */
export function clampToDay(item: Pick<TimedItem, 'start_time' | 'end_time'>, day: Date): Interval | null {
  const base = startOfDay(day);
  const s = (new Date(item.start_time).getTime() - base) / 60000;
  const e = (new Date(item.end_time).getTime() - base) / 60000;
  if (!Number.isFinite(s) || !Number.isFinite(e)) return null;
  const start = Math.max(0, Math.round(s));
  const end = Math.min(DAY_MINUTES, Math.round(e));
  return end > start ? { start, end } : null;
}

/** Unisce gli intervalli che si toccano o si sovrappongono, in ordine. */
export function mergeIntervals(list: readonly Interval[]): Interval[] {
  const sorted = [...list].filter(i => i.end > i.start).sort((a, b) => a.start - b.start);
  const out: Interval[] = [];
  for (const i of sorted) {
    const last = out[out.length - 1];
    if (last && i.start <= last.end) last.end = Math.max(last.end, i.end);
    else out.push({ ...i });
  }
  return out;
}

/** Intervalli occupati di una persona in un giorno (gli eventi "tutto il giorno" non contano). */
export function busyIntervals(items: readonly TimedItem[], day: Date, personId?: string): Interval[] {
  const list: Interval[] = [];
  for (const it of items) {
    if (it.all_day) continue;
    if (personId && it.ownerId !== personId) continue;
    const c = clampToDay(it, day);
    if (c) list.push(c);
  }
  return mergeIntervals(list);
}

/** I buchi liberi dentro l'orario di lavoro, lunghi almeno `minLength` minuti. */
export function freeIntervals(busy: readonly Interval[], work: Interval | null, minLength = MIN_FREE_MINUTES): Interval[] {
  if (!work) return [];
  const out: Interval[] = [];
  let cursor = work.start;
  for (const b of mergeIntervals(busy)) {
    if (b.end <= work.start) continue;
    if (b.start >= work.end) break;
    if (b.start - cursor >= minLength) out.push({ start: cursor, end: Math.min(b.start, work.end) });
    cursor = Math.max(cursor, b.end);
  }
  if (work.end - cursor >= minLength) out.push({ start: cursor, end: work.end });
  return out;
}

/** Le parti occupate che toccano il periodo scelto (sovrapposizioni). */
export function overlapsWith(busy: readonly Interval[], start: number, end: number): Interval[] {
  return mergeIntervals(busy).filter(b => b.start < end && b.end > start);
}

/** Il primo orario, da `from` in poi, in cui sta un impegno lungo `duration` dentro l'orario di lavoro. */
export function firstFreeStart(busy: readonly Interval[], work: Interval | null, from: number, duration: number): number | null {
  if (!work) return null;
  let t = Math.max(from, work.start);
  for (const b of mergeIntervals(busy)) {
    if (b.end <= t) continue;
    if (b.start >= t + duration) break;
    t = b.end;
  }
  return t + duration <= work.end ? t : null;
}

/** La persona e' libera a quell'ora per quella durata? (dentro l'orario di lavoro e senza impegni) */
export function isFreeAt(busy: readonly Interval[], work: Interval | null, at: number, duration: number): boolean {
  if (!work || at < work.start || at + duration > work.end) return false;
  return overlapsWith(busy, at, at + duration).length === 0;
}

/** Il primo orario libero del giorno (per l'intestazione della colonna). */
export function firstFreeOfDay(busy: readonly Interval[], work: Interval | null, minLength = MIN_FREE_MINUTES): number | null {
  return freeIntervals(busy, work, minLength)[0]?.start ?? null;
}

export function formatMinutes(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}:${String(mm).padStart(2, '0')}`;
}

/** "9:30" o "09:30" in minuti. NaN se non valido. */
export function parseTime(hhmm: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return NaN;
  const h = Number(m[1]); const mm = Number(m[2]);
  return h > 23 || mm > 59 ? NaN : h * 60 + mm;
}

/** Il momento esatto (data + minuti dalla mezzanotte). */
export function atMinutes(day: Date, minutes: number): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, minutes, 0, 0);
}
